// The two renderers that return JSX. The rest of the report's formatting is next
// door in .format.ts, where the fast-refresh rule does not apply.
import { Box, Tooltip, Typography } from '@mui/material';
import { ArrowDropDown, ArrowDropUp, Circle } from '@mui/icons-material';

export function ProfitDot({ profit }: { profit: number }) {
  const color = profit > 0 ? '#4caf50' : profit < 0 ? '#f44336' : '#bdbdbd';
  return <Circle sx={{ fontSize: 10, color }} />;
}

/**
 * Colour by sign, print the value. `displayValue` lets a metric whose breakeven
 * isn't zero colour on one number and show another — ROAS breaks even at 1.0, so it
 * colours on (roas - 1) while displaying roas.
 */
export function colorCell(
  value: number | null | undefined,
  format: (n: number) => string,
  displayValue?: number | null,
) {
  if (value == null) return <Typography variant="body2" color="text.disabled">-</Typography>;
  const shown = displayValue == null ? value : Number(displayValue);
  return (
    <Typography
      variant="body2"
      component="span"
      sx={{ color: value > 0 ? 'success.main' : value < 0 ? 'error.main' : 'text.secondary', fontWeight: value !== 0 ? 600 : 400 }}
    >
      {format(shown)}
    </Typography>
  );
}

/**
 * The compared metric: this period above, the one before it below, with the direction
 * between them. `children` lets a metric that colours itself — profit, ROAS — keep doing
 * so on the top line.
 */
export function CompareCell({ value, prev, format, goodDown, hint, children }: {
  value: number | null | undefined;
  prev: number | null | undefined;
  format: (n: number) => string;
  /** Spending less is an improvement; earning less is not. */
  goodDown?: boolean;
  hint?: string;
  children?: React.ReactNode;
}) {
  const now = Number(value || 0);
  const before = Number(prev || 0);
  const dir = now > before ? 1 : now < before ? -1 : 0;
  const better = goodDown ? dir < 0 : dir > 0;
  const Arrow = dir > 0 ? ArrowDropUp : ArrowDropDown;
  return (
    // The pair has to read as one value. Set tight against each other, they group by
    // proximity; spread to fill the row, each number pairs with its neighbour above.
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center',
      alignItems: 'flex-end', width: '100%', lineHeight: 1.15 }}>
      <Box component="span">{children ?? format(now)}</Box>
      <Tooltip title={hint ?? ''} disableHoverListener={!hint}>
        <Box component="span" sx={{ display: 'flex', alignItems: 'center', mt: '-1px',
          ...(hint && { textDecoration: 'underline dotted', textUnderlineOffset: 3 }) }}>
          {dir !== 0 && (
            <Arrow sx={{ fontSize: 14, mx: '-2px', color: better ? 'success.main' : 'error.main' }} />
          )}
          <Typography variant="caption" component="span" color="text.secondary"
            sx={{ fontSize: '0.72rem', lineHeight: 1.15 }}>
            {format(before)}
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  );
}
